import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Image,
  Input,
  Link,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react'
import { AppContainer } from 'components/ui/appContainer'
import { CustomHeading } from 'components/ui/customHeading'
import { SignUpParams, useAuthDispatcher } from 'context/auth'
import { publicEnv } from 'env'
import { useShowErrorMessageIfNeed } from 'hooks/useShowErrorMessageIfNeed'
import { NextPage } from 'next'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/dist/client/router'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { Routes } from 'routes'

type FormInput = SignUpParams & {
  confirmPassword: string
  agreeTerms: boolean
}

const Page: NextPage = () => {
  const { signUp } = useAuthDispatcher()
  const router = useRouter()
  const { showErrorMessageIfNeed } = useShowErrorMessageIfNeed()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<FormInput>({ mode: 'onSubmit' })

  const onSubmit = useCallback(
    async (data: FormInput) => {
      try {
        await signUp({ email: data.email, password: data.password, name: data.name })
        router.push(Routes.signUpCompleted())
      } catch (e) {
        showErrorMessageIfNeed(e)
      }
    },
    [signUp]
  )

  return (
    <>
      <NextSeo title="管理ユーザーの作成 | HoteCo" openGraph={{ url: `${publicEnv.host}${Routes.signUp()}` }} />
      <AppContainer>
        <Box pt={12} pb={20}>
          <Image src="/assets/signup_steps/step1.png" w="full" h="64px" />
          <Spacer mt={10} />
          <CustomHeading as="h2">管理者ユーザーの作成</CustomHeading>
          <Text fontSize={18} mt={12}>
            はじめに、ユーザーを作成してください。
            <br />
            あなたが最初の管理者となりますが、後から他の方を招待したり、他の方に管理者権限を付与することも可能です。
          </Text>

          <Box mt={10} borderColor="borderBase" borderWidth={1} rounded="lg" py={20} px={12}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <HStack spacing={8}>
                <FormControl isInvalid={!!errors.name}>
                  <FormLabel>名前</FormLabel>
                  <Input {...register('name', { required: '名前の入力は必須です' })} placeholder="田中　二郎" />
                  <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>メールアドレス</FormLabel>
                  <Input
                    {...register('email', { required: 'メールアドレスの入力は必須です' })}
                    type="email"
                    placeholder="tantousha@hoteco-wcc.com"
                  />
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>
              </HStack>

              <HStack spacing={8} mt={12}>
                <FormControl isInvalid={!!errors.password}>
                  <FormLabel>パスワード</FormLabel>
                  <Input
                    {...register('password', { required: 'パスワードの入力は必須です' })}
                    type="password"
                    autoComplete="new-password"
                  />
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.confirmPassword}>
                  <FormLabel>パスワード（確認）</FormLabel>
                  <Input
                    {...register('confirmPassword', {
                      required: 'パスワード（確認）の入力は必須です',
                      validate: data => {
                        const password = getValues('password')
                        if (password !== data) return 'パスワードが一致していません'
                      },
                    })}
                    type="password"
                    autoComplete="new-password"
                  />
                  <FormErrorMessage>{errors.confirmPassword?.message}</FormErrorMessage>
                </FormControl>
              </HStack>

              <VStack mt={12} spacing={12}>
                <FormControl isInvalid={!!errors.agreeTerms} w="auto">
                  <HStack>
                    <Checkbox {...register('agreeTerms', { required: '同意が必要です' })}>
                      <Text>
                        <Link href={Routes.terms()} isExternal color="link">
                          HoteCo利用規約
                        </Link>
                        、
                        <Link color="link" href={Routes.privacy()} isExternal>
                          個人情報保護方針
                        </Link>
                        に同意する
                      </Text>
                    </Checkbox>
                  </HStack>
                  <FormErrorMessage>{errors.agreeTerms?.message}</FormErrorMessage>
                </FormControl>

                <Button type="submit" isLoading={isSubmitting} w="346px">
                  作成を完了する
                </Button>
              </VStack>
            </form>
          </Box>
        </Box>
      </AppContainer>
    </>
  )
}

export default Page
